import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { EventTrackingService } from '../../../../shared/event-tracking/event-tracking.service';
import { Output } from '../../../../team-apis/otterdam/outputs/output';
import { isAnnotationOutput } from '../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { SamplingWorkspaceStore } from '../../../sampling-workspace/state/sampling-workspace-store';
import { addFeedbackIcon } from '../../../shared/icons/feedback/add-feedback-icon';
import { FeatureFlagService } from '../../../shared/misc-services/feature-flag.service';
import { FieldFeedbackService } from '../../services/field-feedback.service';
import { FieldFeedback } from '../../types/field-feedback';
import { UpsertFieldFeedbackComponent } from '../add-field-feedback/upsert-field-feedback.component';
import { FieldFeedbackListComponent } from '../field-feedback-list/field-feedback-list.component';

@Component({
  selector: 'h3-field-feedback-output-footer',
  imports: [
    MatBadgeModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    CommonModule,
    MatDividerModule,
    FieldFeedbackListComponent,
    UpsertFieldFeedbackComponent,
  ],
  templateUrl: './field-feedback-output-footer.component.html',
  styleUrl: './field-feedback-output-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldFeedbackOutputFooterComponent
  extends InjectableSuperclass
  implements OnInit
{
  @Input({ required: true }) output!: Output;

  private readonly store = inject(TaskWorkspaceStore);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly fieldFeedbackService = inject(FieldFeedbackService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly sampleStore = inject(SamplingWorkspaceStore);

  feedbackCount$!: Observable<number>;
  feedbackItems$!: Observable<FieldFeedback[]>;
  isButtonVisible$ = combineLatest([
    this.store('fieldFeedback').$,
    this.sampleStore.hasSampleFinalized$(),
  ]).pipe(
    map(
      // eslint-disable-next-line complexity -- easy to track to put together
      ([fieldFeedback, hasSampleFinalized]) =>
        this.featureFlagService.hasFlag('new-sampling-nav') &&
        !isAnnotationOutput(this.output) &&
        (!!fieldFeedback[this.output.id]?.length ||
          (!fieldFeedback[this.output.id]?.length &&
            !hasSampleFinalized &&
            this.store.state().workspace === 'sampling')),
    ),
  );
  mode$ = this.store('fieldFeedbackPopover')('target').$;
  addFeedbackIcon = addFeedbackIcon;

  ngOnInit() {
    this.feedbackItems$ = this.store('fieldFeedback')(this.output.id).$.pipe(
      filter(isDefined),
    );
    this.feedbackCount$ = this.feedbackItems$.pipe(
      map((feedbacks) => feedbacks.length),
    );
  }

  registerMenuOpened(trigger: MatMenuTrigger) {
    this.fieldFeedbackService.open(this.output.id);
    const popoverStore = this.store('fieldFeedbackPopover');
    const popoverOutputIdStore = popoverStore('outputId');

    this.subscribeTo(
      popoverOutputIdStore.$.pipe(takeUntil(trigger.menuClosed)),
      (outputId) => {
        if (outputId !== this.output.id) {
          trigger.closeMenu();
        }
      },
    );

    this.setUpResizeSubscription(trigger);

    if (popoverStore.state().target === 'list') {
      this.eventTrackingService.track({
        id: 'open_feedback_list',
        bucket: 'quality_feedback',
        type: 'field',
      });
    }
  }

  private setUpResizeSubscription(trigger: MatMenuTrigger) {
    this.subscribeTo(
      this.store('fieldFeedbackPopover').$.pipe(
        filter((popover) => popover.outputId === this.output.id),
        distinctUntilChanged(
          (previous, current) => previous.target === current.target,
        ),
        takeUntil(trigger.menuClosed),
      ),
      // Force the mat menu to recalculate position by dispatching resize event.
      // This is needed because after resizing the menu, it might be clipped at
      // the top or bottom of the window.
      () => window.dispatchEvent(new Event('resize')),
    );
  }
}
